import type { Milestone } from '../types';

type AttestationSide = 'company' | 'person';

export type AttestableMilestone = Pick<
  Milestone,
  'id' | 'milestoneNumber' | 'companyAttestation' | 'personAttestation' | 'deliveryStatus' | 'stageReplacement' | 'continuedBy'
>;

export const canAttestMilestone = (
  milestone: AttestableMilestone,
  side: AttestationSide
): boolean => {
  if (milestone.stageReplacement || milestone.continuedBy) return false;
  if (!milestone.deliveryStatus) {
    return side === 'company'
      ? milestone.companyAttestation !== 'approved'
      : milestone.personAttestation !== 'approved';
  }

  if (
    milestone.companyAttestation === 'approved' &&
    milestone.personAttestation === 'approved'
  ) {
    return false;
  }

  // Accepting the remedy withdraws the customer's objection; it does not
  // attest for the company or permit a payout before both parties approve.
  if (milestone.deliveryStatus === 'under_resolution') {
    return side === 'person' && milestone.personAttestation !== 'approved';
  }

  if (milestone.deliveryStatus === 'disputed') {
    return side === 'person' &&
      milestone.companyAttestation === 'approved' &&
      milestone.personAttestation === 'rejected';
  }

  if (
    [
      'not_started',
      'conflict',
      'payout_processing',
      'payout_failed',
      'paid'
    ].includes(milestone.deliveryStatus)
  ) {
    return false;
  }

  const currentSideAttestation = side === 'company'
    ? milestone.companyAttestation
    : milestone.personAttestation;
  if (currentSideAttestation === 'approved') return false;

  // The attestation fields are authoritative. If a legacy status was advanced
  // too early, the missing party must still be able to complete this milestone.
  const otherSideApproved = side === 'company'
    ? milestone.personAttestation === 'approved'
    : milestone.companyAttestation === 'approved';
  if (otherSideApproved) return true;

  return side === 'company'
    ? ['in_progress', 'ready_for_attestation', 'ready_for_reattestation'].includes(
          milestone.deliveryStatus
        )
    : [
          'in_progress',
          'ready_for_attestation',
          'ready_for_reattestation',
          'seller_attested'
        ].includes(milestone.deliveryStatus);
};

export const nextMilestoneAwaitingAttestation = <T extends AttestableMilestone>(
  milestones: readonly T[],
  side: AttestationSide
): T | undefined =>
  milestones
    .slice()
    .sort((a, b) => a.milestoneNumber - b.milestoneNumber)
    .find((milestone) => {
      const currentSide = side === 'company'
        ? milestone.companyAttestation
        : milestone.personAttestation;
      const otherSide = side === 'company'
        ? milestone.personAttestation
        : milestone.companyAttestation;
      return currentSide !== 'approved' || otherSide !== 'approved';
    });

export const milestoneAttestationActionLabel = (
  baseLabel: string,
  milestone: Pick<Milestone, 'milestoneNumber'>
) => `${baseLabel} #${milestone.milestoneNumber}`;
